import React, { useState, useEffect } from "react";
import { FiZap, FiBookmark, FiLoader, FiCheck } from "react-icons/fi";
import { getIntegrationIconSrc } from "../../utils/integrationIcons";
import InlineIntegrationConnector from "../integrations/InlineIntegrationConnector";

// Local insight type for display
interface DisplayInsight {
  id: string;
  title: string;
  summary?: {
    headline?: string;
    narrative?: string;
  } | string;
  data_sources?: string[];
  created_at: string;
}

// Type-safe icon wrappers
const ZapIcon = FiZap as React.FC<React.SVGProps<SVGSVGElement>>;
const BookmarkIcon = FiBookmark as React.FC<React.SVGProps<SVGSVGElement>>;
const LoaderIcon = FiLoader as React.FC<React.SVGProps<SVGSVGElement>>;
const CheckIcon = FiCheck as React.FC<React.SVGProps<SVGSVGElement>>;

// Chain of thought steps for insight generation (present tense)
const INSIGHT_COT_STEPS = [
  "Connecting to data sources...",
  "Fetching latest metrics...",
  "Analyzing patterns and trends...",
  "Generating actionable insights...",
  "Finalizing recommendations...",
];

// Past tense versions of the first word
const PAST_TENSE_MAP: Record<string, string> = {
  "Connecting": "Connected",
  "Fetching": "Fetched",
  "Analyzing": "Analyzed",
  "Generating": "Generated",
  "Finalizing": "Finalized",
  "Understanding": "Understood",
  "Optimizing": "Optimized",
};

// Convert first word to past tense
const toPastTense = (step: string): string => {
  const firstWord = step.split(" ")[0];
  const pastTense = PAST_TENSE_MAP[firstWord];
  if (pastTense) {
    return step.replace(firstWord, pastTense);
  }
  return step;
};

interface Integration {
  id: string;
  provider: string;
  name: string;
}

interface InsightsSidebarProps {
  integrations: Integration[];
  selectedIntegrations: string[];
  onToggleIntegration: (id: string) => void;
  generatedInsights: DisplayInsight[];
  pastInsights: DisplayInsight[];
  onAddToEmail: (insight: DisplayInsight) => void | Promise<void>;
  onSaveInsight: (insight: DisplayInsight) => Promise<DisplayInsight>;
  isGenerating: boolean;
  setIsGenerating: (value: boolean) => void;
  setGeneratedInsights: (insights: DisplayInsight[]) => void;
  setPastInsights: (insights: DisplayInsight[]) => void;
  /** Callback when a new integration is connected inline */
  onIntegrationConnected?: (provider: string) => void;
}

/**
 * InsightsSidebar - Three separate BLACK brutalist sections:
 * 1. Generate New Insight
 * 2. Just Generated (only when insights exist)
 * 3. Past Insights
 */
const InsightsSidebar: React.FC<InsightsSidebarProps> = ({
  integrations,
  selectedIntegrations,
  onToggleIntegration,
  generatedInsights,
  pastInsights,
  onAddToEmail,
  onSaveInsight,
  isGenerating,
  setIsGenerating,
  setGeneratedInsights,
  setPastInsights,
  onIntegrationConnected,
}) => {
  const [insightQuestion, setInsightQuestion] = useState("");
  const [generationSteps, setGenerationSteps] = useState<string[]>([]);
  const [completedStepIndex, setCompletedStepIndex] = useState(-1);
  const [showProgress, setShowProgress] = useState(false);
  const [pendingInsightsLocal, setPendingInsightsLocal] = useState<DisplayInsight[] | null>(null);
  const [savingInsightId, setSavingInsightId] = useState<string | null>(null);
  const [savedInsightIds, setSavedInsightIds] = useState<Set<string>>(new Set());
  const [recentlySavedIds, setRecentlySavedIds] = useState<Set<string>>(new Set());
  const [allStepsAdded, setAllStepsAdded] = useState(false);

  // Effect to mark steps complete one by one every 3 seconds
  useEffect(() => {
    if (!showProgress || generationSteps.length === 0) {
      return;
    }

    // Find the next step to complete
    const nextToComplete = completedStepIndex + 1;
    
    // If we haven't completed this step yet and it exists
    if (nextToComplete < generationSteps.length) {
      const timer = setTimeout(() => {
        setCompletedStepIndex(nextToComplete);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [showProgress, generationSteps.length, completedStepIndex]);

  // Effect to show insights after all steps complete
  useEffect(() => {
    if (!allStepsAdded || !pendingInsightsLocal) return;
    
    // Check if all steps are complete
    const allComplete = completedStepIndex >= generationSteps.length - 1;
    
    if (allComplete && generationSteps.length > 0) {
      // Show final step complete, then display insights
      setTimeout(() => {
        setGeneratedInsights(pendingInsightsLocal);
        setPendingInsightsLocal(null);
        setShowProgress(false);
        setGenerationSteps([]);
        setCompletedStepIndex(-1);
        setAllStepsAdded(false);
      }, 500);
    }
  }, [completedStepIndex, pendingInsightsLocal, generationSteps.length, allStepsAdded, setGeneratedInsights]);

  // Handle saving an insight to database and past insights
  const handleSaveInsight = async (insight: DisplayInsight) => {
    setSavingInsightId(insight.id);
    try {
      await onSaveInsight(insight);
      
      // Add to saved set
      setSavedInsightIds(prev => {
        const newSet = new Set(prev);
        newSet.add(insight.id);
        return newSet;
      });
      
      // Add to recently saved for "JUST SAVED" tag
      setRecentlySavedIds(prev => {
        const newSet = new Set(prev);
        newSet.add(insight.id);
        return newSet;
      });
      
      // Add to past insights at the top immediately
      setPastInsights([insight, ...pastInsights.filter(i => i.id !== insight.id)]);
      
      // Remove "JUST SAVED" tag after 30 seconds
      setTimeout(() => {
        setRecentlySavedIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(insight.id);
          return newSet;
        });
      }, 30000);
    } catch (error) {
      console.error("Failed to save insight:", error);
    } finally {
      setSavingInsightId(null);
    }
  };

  // Capitalize first letter of integration name
  const capitalizeProvider = (provider: string): string => {
    if (!provider) return "";
    // Handle special cases
    const specialCases: Record<string, string> = {
      github: "GitHub",
      gitlab: "GitLab",
      clickup: "ClickUp",
      hubspot: "HubSpot",
      quickbooks: "QuickBooks",
      mailchimp: "Mailchimp",
      mixpanel: "Mixpanel",
      productboard: "Productboard",
      typeform: "Typeform",
      zendesk: "Zendesk",
      intercom: "Intercom",
      airtable: "Airtable",
      bitbucket: "Bitbucket",
    };
    const key = provider.toLowerCase();
    return specialCases[key] || provider.charAt(0).toUpperCase() + provider.slice(1).toLowerCase();
  };

  // Get integration icon from assets
  const renderIntegrationIcon = (provider: string) => {
    const iconSrc = getIntegrationIconSrc(provider);
    if (iconSrc) {
      return (
        <img 
          src={iconSrc} 
          alt={capitalizeProvider(provider)} 
          className="su-integration-icon"
        />
      );
    }
    // Fallback to Zap icon if no image found
    return <ZapIcon />;
  };

  const getSourceBadgeClass = (source: string) => {
    const provider = source.toLowerCase();
    if (provider.includes("github")) return "su-insight-badge-github";
    if (provider.includes("slack")) return "su-insight-badge-slack";
    if (provider.includes("clickup")) return "su-insight-badge-clickup";
    if (provider.includes("stripe")) return "su-insight-badge-stripe";
    if (provider.includes("analytics")) return "su-insight-badge-analytics";
    return "su-insight-badge-default";
  };

  // Check if a string looks like a UUID or ID
  const isUUID = (str: string): boolean => {
    if (!str) return false;
    // UUID format: 8-4-4-4-12 hex characters
    if (str.length === 36 && /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i.test(str)) {
      return true;
    }
    // Long hex string (likely an ID)
    if (str.length > 20 && /^[a-f0-9-]+$/i.test(str)) {
      return true;
    }
    return false;
  };

  const getInsightSource = (insight: DisplayInsight): string => {
    // Try to get a clean source name from data_sources
    if (insight.data_sources && insight.data_sources.length > 0) {
      for (const source of insight.data_sources) {
        if (source && !isUUID(source)) {
          return capitalizeProvider(source);
        }
      }
    }
    
    // Try to get type or category from insight (if available)
    const insightAny = insight as any;
    if (insightAny.type && !isUUID(insightAny.type)) {
      return capitalizeProvider(insightAny.type);
    }
    if (insightAny.category && !isUUID(insightAny.category)) {
      return capitalizeProvider(insightAny.category);
    }
    
    // Default fallback
    return "Insight";
  };

  const getInsightDescription = (insight: DisplayInsight): string => {
    if (typeof insight.summary === "string") {
      return insight.summary;
    }
    if (insight.summary?.narrative) {
      return insight.summary.narrative;
    }
    if (insight.summary?.headline) {
      return insight.summary.headline;
    }
    return "No description available";
  };

  const handleGenerateInsights = async () => {
    if (selectedIntegrations.length === 0) {
      return;
    }

    setIsGenerating(true);
    setShowProgress(true);
    setGenerationSteps([]);
    setCompletedStepIndex(-1);
    setPendingInsightsLocal(null);
    setAllStepsAdded(false);

    // Show ALL chain of thought steps immediately so animation can run
    setGenerationSteps([...INSIGHT_COT_STEPS]);

    const API_BASE_URL = process.env.REACT_APP_API_URL || 
      (window.location.hostname === "localhost" ? "http://localhost:4000" : "https://api.sendupdates.ai");

    try {
      const { data: sessionData } = await import("../../lib/supabaseClient").then(
        (mod) => mod.default.auth.getSession()
      );
      const token = sessionData?.session?.access_token;

      if (!token) {
        console.error("No auth token available");
        setGenerationSteps(prev => [...prev, "❌ Authentication required"]);
        setAllStepsAdded(true);
        setIsGenerating(false);
        return;
      }

      // 500s timeout covering the ENTIRE stream (connection + reading)
      const controller = new AbortController();
      const streamTimeout = setTimeout(() => controller.abort(), 500000);

      let response: Response;
      try {
        response = await fetch(`${API_BASE_URL}/insights/generate-stream`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            type: "comparison",
            category: "operational",
            integrationIds: selectedIntegrations,
            question: insightQuestion || "Generate relevant business insights",
          }),
          signal: controller.signal,
        });
      } catch (fetchErr) {
        clearTimeout(streamTimeout);
        throw fetchErr;
      }

      if (!response.ok) {
        clearTimeout(streamTimeout);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (!reader) {
        clearTimeout(streamTimeout);
        throw new Error("No response body");
      }

      let buffer = "";
      let done = false;
      let receivedInsights: DisplayInsight[] = [];

      while (!done) {
        const result = await reader.read();
        done = result.done;

        if (done) break;
        const value = result.value;

        buffer += decoder.decode(value, { stream: true });

        const lines = buffer.split("\n");
        buffer = lines.pop() || "";

        for (let i = 0; i < lines.length; i++) {
          const line = lines[i].trim();

          if (line.startsWith("event:")) {
            const eventType = line.substring(6).trim();
            const dataLine = lines[i + 1];

            if (dataLine?.startsWith("data:")) {
              const jsonData = dataLine.substring(5).trim();
              try {
                const eventData = JSON.parse(jsonData);

                if (eventType === "complete") {
                  if (eventData.data?.insights) {
                    receivedInsights = eventData.data.insights;
                  } else if (eventData.data?.insight) {
                    receivedInsights = [eventData.data.insight];
                  }
                } else if (eventType === "error") {
                  console.error("SSE error:", eventData.error);
                }
              } catch (parseError) {
                console.error("Failed to parse SSE data:", parseError);
              }
              i++;
            }
          }
        }
      }

      // Stream fully consumed — clear the timeout
      clearTimeout(streamTimeout);

      // Mark all steps as added so completion effect can trigger
      setAllStepsAdded(true);
      
      // Store insights to be shown after all steps complete
      if (receivedInsights.length > 0) {
        setPendingInsightsLocal(receivedInsights);
      } else {
        // No insights received, add error step
        setGenerationSteps(prev => [...prev, "❌ No insights generated"]);
        setTimeout(() => {
          setShowProgress(false);
          setGenerationSteps([]);
          setCompletedStepIndex(-1);
          setAllStepsAdded(false);
        }, 5000);
      }
    } catch (error) {
      console.error("Failed to generate insights:", error);
      setGenerationSteps(prev => [...prev, "❌ Failed to generate insights"]);
      setAllStepsAdded(true);
      setTimeout(() => {
        setShowProgress(false);
        setGenerationSteps([]);
        setCompletedStepIndex(-1);
        setAllStepsAdded(false);
      }, 5000);
    } finally {
      setIsGenerating(false);
    }
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
    
    if (diffInDays === 0) return "Today";
    if (diffInDays === 1) return "Yesterday";
    if (diffInDays < 7) return `${diffInDays} days ago`;
    if (diffInDays < 30) return `${Math.floor(diffInDays / 7)} weeks ago`;
    return `${Math.floor(diffInDays / 30)} months ago`;
  };

  return (
    <>
      {/* ========================================
          GENERATE NEW INSIGHT SECTION
          ======================================== */}
      <div className="su-section">
        <h3 className="su-insight-section-title">GENERATE NEW INSIGHT</h3>

        {/* Select Sources */}
        <div className="su-insight-sources">
          <label className="su-insight-label">SELECT SOURCES</label>
          <div className="su-insight-source-buttons">
            {integrations.map((integration) => (
              <button
                key={integration.id}
                type="button"
                className={`su-insight-source-btn ${
                  selectedIntegrations.includes(integration.id) ? "active" : ""
                }`}
                onClick={() => onToggleIntegration(integration.id)}
              >
                {renderIntegrationIcon(integration.provider)}
                <span>{capitalizeProvider(integration.provider)}</span>
              </button>
            ))}

            {/* Inline Integration Connector */}
            <InlineIntegrationConnector
              connectedProviders={integrations.map(i => i.provider)}
              onConnectionSuccess={onIntegrationConnected}
              variant="compact"
              className="su-insight-source-btn su-insight-connect-btn"
            />
          </div>
          {integrations.length === 0 && (
            <p className="su-insight-empty-hint">
              Click + to connect your first integration
            </p>
          )}
        </div>

        {/* Question Input */}
        <div className="su-insight-question">
          <label className="su-insight-label">WHAT INSIGHT DO YOU NEED?</label>
          <input
            type="text"
            className="su-insight-input"
            placeholder="e.g., Engineering metrics, revenue growth.."
            value={insightQuestion}
            onChange={(e) => setInsightQuestion(e.target.value)}
          />
        </div>

        {/* Generate Button */}
        <button
          type="button"
          className={`su-insight-generate-btn ${isGenerating ? "loading" : ""}`}
          onClick={handleGenerateInsights}
          disabled={selectedIntegrations.length === 0 || isGenerating}
        >
          {isGenerating ? (
            <>
              <LoaderIcon className="su-spinner" />
              <span>GENERATING...</span>
            </>
          ) : (
            <>
              <ZapIcon />
              <span>GENERATE INSIGHTS</span>
            </>
          )}
        </button>

        {/* Chain of Thought Progress */}
        {showProgress && generationSteps.length > 0 && (
          <div className="su-insight-cot-progress">
            <div className="su-insight-cot-steps">
              {generationSteps.map((step, index) => {
                const isComplete = index <= completedStepIndex;
                const isError = step.startsWith("❌");
                // Convert to past tense when complete
                const displayText = isComplete && !isError ? toPastTense(step) : step;
                
                return (
                  <div key={index} className={`su-insight-cot-step ${isComplete ? 'completed' : ''}`}>
                    <span className="su-insight-cot-icon">
                      {isError ? (
                        <span className="su-insight-cot-error">!</span>
                      ) : isComplete ? (
                        <CheckIcon className="su-insight-cot-check" />
                      ) : (
                        <LoaderIcon className="su-spinner su-insight-cot-loader" />
                      )}
                    </span>
                    <span className={`su-insight-cot-text ${isError ? 'error' : ''}`}>
                      {displayText}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* ========================================
          JUST GENERATED SECTION
          ======================================== */}
      {generatedInsights.length > 0 && (
        <div className="su-section">
          <div className="su-insight-generated-header">
            <ZapIcon />
            <span>JUST GENERATED</span>
          </div>

          <div className="su-insight-list">
            {generatedInsights.map((insight) => {
              const isSaving = savingInsightId === insight.id;
              const isSaved = savedInsightIds.has(insight.id);
              
              return (
                <div key={insight.id} className="su-insight-card su-insight-card-generated">
                  <div className="su-insight-card-header">
                    <span className={`su-insight-badge ${getSourceBadgeClass(getInsightSource(insight))}`}>
                      {getInsightSource(insight)}
                    </span>
                    <button 
                      type="button" 
                      className={`su-insight-bookmark ${isSaved ? 'saved' : ''}`}
                      title={isSaved ? "Saved" : "Save to Past Insights"}
                      onClick={() => !isSaved && !isSaving && handleSaveInsight(insight)}
                      disabled={isSaving || isSaved}
                    >
                      {isSaving ? (
                        <LoaderIcon className="su-spinner" />
                      ) : isSaved ? (
                        <CheckIcon />
                      ) : (
                        <BookmarkIcon />
                      )}
                    </button>
                  </div>
                  <h4 className="su-insight-card-title">{insight.title}</h4>
                  <p className="su-insight-card-description">
                    {getInsightDescription(insight)}
                  </p>
                  <button
                    type="button"
                    className="su-insight-add-btn"
                    onClick={() => onAddToEmail(insight)}
                  >
                    ADD TO EMAIL
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ========================================
          PAST INSIGHTS SECTION
          ======================================== */}
      <div className="su-section">
        <h3 className="su-insight-section-title">PAST INSIGHTS</h3>

        <div className="su-insight-list">
          {pastInsights.length === 0 ? (
            <p className="su-insight-empty">No past insights yet</p>
          ) : (
            pastInsights.map((insight) => {
              const isRecentlySaved = recentlySavedIds.has(insight.id);
              
              return (
                <div key={insight.id} className="su-insight-card su-insight-card-generated">
                  <div className="su-insight-card-header">
                    <span className={`su-insight-badge ${getSourceBadgeClass(getInsightSource(insight))}`}>
                      {getInsightSource(insight)}
                    </span>
                    <div className="su-insight-header-right">
                      {isRecentlySaved && (
                        <span className="su-insight-just-saved-tag">JUST SAVED</span>
                      )}
                      <span className="su-insight-date">
                        {formatTimeAgo(insight.created_at)}
                      </span>
                    </div>
                  </div>
                  <h4 className="su-insight-card-title">{insight.title}</h4>
                  <p className="su-insight-card-description">
                    {getInsightDescription(insight)}
                  </p>
                  <button
                    type="button"
                    className="su-insight-add-btn"
                    onClick={() => onAddToEmail(insight)}
                  >
                    ADD TO EMAIL
                  </button>
                </div>
              );
            })
          )}
        </div>
      </div>
    </>
  );
};

export default InsightsSidebar;
